/**
 * platformPolicy — the single source of truth for numbers that would live
 * in a `platform_policy` MongoDB config document when the backend arrives.
 *
 * Design intent: any threshold, cap, duration, or cadence that the board
 * approves lives here and is edited from the Admin UI (see
 * `components/admin/AdminSettings.jsx`). No hard-coded caps in the
 * wizard, admin views, or public copy. Change the number here, and every
 * consumer picks up the new number on the next render.
 *
 * When the backend arrives, swap the four exports below for API calls:
 *   GET /api/platform-policy
 *   PUT /api/platform-policy { patch }
 * and keep the same subscribe/getter shape so no consumer has to change.
 *
 * Phase 0 defaults reflect the board approvals of Aug 1 2026:
 *   Beta:
 *     - Grant cap:            $500 per campaign
 *     - Documentation reqd:   n/a (nothing above the cap anyway)
 *   Pilot (post-beta, exact cap TBD):
 *     - Grant cap:            TBD (null until the board sets it)
 *     - Documentation reqd:   any campaign requesting > $500
 *   Both phases:
 *     - Campaign duration:    30 days
 *     - Extensions:           one 15-day extension per campaign
 *     - Extension approval:   requires admin sign-off
 *     - Disbursement cadence: weekly, in equal installments
 */

const STORAGE_KEY = "mra.platformPolicy.v2";

export const DEFAULT_POLICY = {
  activePhase: "beta", // "beta" | "pilot"
  beta: {
    grantCap: 500,
    documentationThreshold: null, // n/a during beta — cap = threshold
  },
  pilot: {
    grantCap: null, // TBD — board to set before pilot launch
    documentationThreshold: 500, // require docs for campaigns > $500
  },
  campaignDurationDays: 30,
  extensionDays: 15,
  extensionsAllowed: 1, // per campaign, per lifecycle
  extensionRequiresAdminApproval: true,
  disbursementCadence: "weekly-equal-installments",
  lastUpdatedAt: null,
  lastUpdatedBy: null,
};

const listeners = new Set();

const read = () => {
  if (typeof window === "undefined") return { ...DEFAULT_POLICY };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_POLICY };
    // Shallow-merge so any new keys added to DEFAULT_POLICY over time also
    // appear on older stored blobs.
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_POLICY,
      ...parsed,
      beta:  { ...DEFAULT_POLICY.beta,  ...(parsed.beta  || {}) },
      pilot: { ...DEFAULT_POLICY.pilot, ...(parsed.pilot || {}) },
    };
  } catch {
    return { ...DEFAULT_POLICY };
  }
};

const write = (next) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore in prototype */
  }
  listeners.forEach((cb) => cb(next));
};

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

export const getPolicy = () => read();

// Convenience selectors used by consumers (the wizard, admin views, docs).
export const getActiveCap = () => {
  const p = read();
  return p[p.activePhase]?.grantCap ?? null;
};

export const getActiveDocsThreshold = () => {
  const p = read();
  return p[p.activePhase]?.documentationThreshold ?? null;
};

// Returns { requiresDocs, threshold } for a given proposed goal amount.
export const evaluateGoal = (goal) => {
  const num = Number(goal) || 0;
  const p = read();
  const cap = p[p.activePhase]?.grantCap ?? null;
  const threshold = p[p.activePhase]?.documentationThreshold ?? null;
  return {
    goal: num,
    cap,
    exceedsCap: cap != null && num > cap,
    threshold,
    requiresDocs: threshold != null && num > threshold,
    activePhase: p.activePhase,
  };
};

// Merge-patch the policy. Any subtree may be supplied.
export const updatePolicy = (patch, editorName = "Terri") => {
  const cur = read();
  const next = {
    ...cur,
    ...patch,
    beta:  { ...cur.beta,  ...(patch.beta  || {}) },
    pilot: { ...cur.pilot, ...(patch.pilot || {}) },
    lastUpdatedAt: new Date().toISOString(),
    lastUpdatedBy: editorName,
  };
  write(next);
  return next;
};

// Roll the entire config back to Phase 0 defaults (used by the admin "Reset"
// button). Preserves the audit trail (lastUpdatedAt / By).
export const resetPolicy = (editorName = "Terri") => {
  const next = {
    ...DEFAULT_POLICY,
    lastUpdatedAt: new Date().toISOString(),
    lastUpdatedBy: editorName,
  };
  write(next);
  return next;
};

export const subscribePolicy = (cb) => {
  listeners.add(cb);
  return () => listeners.delete(cb);
};
